import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Download, Printer, ArrowLeft, Lock, Share2, Copy, Twitter, Facebook, Linkedin, Instagram, MessageCircle, Mail } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getInstrument, getCourse } from "@/data/curriculum";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const EmailShare = ({ certId }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = (e) => {
    e.preventDefault();
    setBusy(true);
    api.post(`/certificates/${certId}/email`, { email, message })
      .then(() => { toast.success(`Certificate sent to ${email}`); setEmail(""); setMessage(""); })
      .catch((err) => toast.error(err.response?.data?.detail?.toString?.() || "Could not send — check the email address"))
      .finally(() => setBusy(false));
  };
  return (
    <form onSubmit={send} className="mt-5 border-t border-border pt-5" data-testid="certificate-email-form">
      <p className="flex items-center gap-2 font-serif text-base"><Mail size={16} className="text-primary" /> Email it to your teacher, family or church music team</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their@email.com" data-testid="certificate-email-input"
          className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
        <Button type="submit" disabled={busy || !certId} data-testid="certificate-email-send" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">{busy ? "Sending…" : "Send"}</Button>
      </div>
      <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} placeholder="Add a short message (optional)" data-testid="certificate-email-message"
        className="mt-2 w-full rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
      <p className="mt-2 text-xs text-muted-foreground">Up to 5 emails a day. Your name is shown as the sender.</p>
    </form>
  );
};

export function CertificateArt({ svgRef, name, courseTitle, tagline, dateStr }) {
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1000 700"
      className="h-auto w-full"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="certificate-art"
    >
      <rect x="0" y="0" width="1000" height="700" fill="#0B0B0D" />
      <rect x="26" y="26" width="948" height="648" fill="none" stroke="#F97316" strokeWidth="3" />
      <rect x="40" y="40" width="920" height="620" fill="none" stroke="#3F3F46" strokeWidth="1.5" />
      <text x="500" y="120" textAnchor="middle" fill="#F97316" fontFamily="monospace" fontSize="20" letterSpacing="8">THE JT HUB</text>
      <text x="500" y="215" textAnchor="middle" fill="#FAFAFA" fontFamily="Georgia, serif" fontSize="58" fontWeight="bold">Certificate</text>
      <text x="500" y="258" textAnchor="middle" fill="#A1A1AA" fontFamily="monospace" fontSize="18" letterSpacing="6">OF COMPLETION</text>
      <line x1="360" y1="290" x2="640" y2="290" stroke="#F97316" strokeWidth="2" />
      <text x="500" y="345" textAnchor="middle" fill="#A1A1AA" fontFamily="Georgia, serif" fontSize="22" fontStyle="italic">This is proudly presented to</text>
      <text x="500" y="425" textAnchor="middle" fill="#FAFAFA" fontFamily="Georgia, serif" fontSize="52" fontWeight="bold">{name}</text>
      <text x="500" y="480" textAnchor="middle" fill="#A1A1AA" fontFamily="Georgia, serif" fontSize="22">for successfully completing</text>
      <text x="500" y="525" textAnchor="middle" fill="#F97316" fontFamily="Georgia, serif" fontSize="30" fontWeight="bold">{courseTitle}</text>
      <text x="500" y="558" textAnchor="middle" fill="#A1A1AA" fontFamily="monospace" fontSize="16" letterSpacing="3">{tagline}</text>
      <line x1="150" y1="620" x2="360" y2="620" stroke="#52525B" strokeWidth="1" />
      <text x="255" y="645" textAnchor="middle" fill="#A1A1AA" fontFamily="Georgia, serif" fontSize="18" fontStyle="italic" data-testid="certificate-signature">Jason Thompson, The JT Hub</text>
      <line x1="640" y1="620" x2="850" y2="620" stroke="#52525B" strokeWidth="1" />
      <text x="745" y="645" textAnchor="middle" fill="#A1A1AA" fontFamily="monospace" fontSize="16" data-testid="certificate-date">{dateStr}</text>
    </svg>
  );
}

export default function Certificate() {
  const { instrumentId, courseId } = useParams();
  const { user, progress } = useAuth();
  const svgRef = useRef(null);
  const [completedAt, setCompletedAt] = useState(null);
  const [certId, setCertId] = useState(null);
  useEffect(() => {
    if (!user) return;
    const key = `${instrumentId}:${courseId}`;
    const lessons = getCourse(instrumentId, courseId)?.lessons || [];
    api.get("/certificates").then(({ data }) => {
      const c = data.certificates.find((x) => x.course_key === key);
      if (c) { setCompletedAt(c.completed_at); setCertId(c.id); }
      else if (lessons.length) api.post("/certificates/claim", { course_key: key, lesson_ids: lessons.map((l) => l.id) }).then((r) => { setCompletedAt(r.data.completed_at); setCertId(r.data.id); }).catch(() => {});
    }).catch(() => {});
  }, [user, instrumentId, courseId]);

  const instrument = getInstrument(instrumentId);
  const course = getCourse(instrumentId, courseId);

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="animate-pulse text-primary">Loading…</div></div>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (!instrument || !course) return <Navigate to="/dashboard" replace />;

  const completed = new Set(progress?.completed_lessons || []);
  const done = course.lessons.filter((l) => completed.has(l.id)).length;
  const complete = done === course.lessons.length;

  const name = user?.name || "Student";
  const courseTitle = course.mini ? course.title : `${instrument.label} — ${course.title}`;
  const dateStr = new Date(completedAt || Date.now()).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const downloadPng = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 2000;
      canvas.height = 1400;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0B0B0D";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement("a");
      a.download = `JTHub-certificate-${instrument.label}-${course.id}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  const shareUrl = `${window.location.origin}${certId ? `/c/${certId}` : ""}`;
  const shareText = `I earned my ${courseTitle} certificate on The JT Hub! 🎼`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "The JT Hub certificate", text: shareText, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => toast.success("Copied — paste it anywhere to share"));
    }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => toast.success("Achievement link copied"));
  };
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, "_blank", "noopener");
  const shareInstagram = () => {
    downloadPng();
    navigator.clipboard?.writeText(`${shareText} ${shareUrl}`).catch(() => {});
    toast.success("Certificate image downloaded and caption copied — post it on Instagram and paste the caption");
    setTimeout(() => window.open("https://www.instagram.com/", "_blank", "noopener"), 600);
  };
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank", "noopener");

  return (
    <div className="min-h-screen text-foreground">
      <div className="print:hidden"><Navbar /></div>
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-10 lg:py-14">
        <Link to="/dashboard" data-testid="certificate-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary print:hidden">
          <ArrowLeft size={15} /> Back to dashboard
        </Link>

        {!complete ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="certificate-locked">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Lock size={24} /></span>
            <h1 className="mt-5 font-serif text-3xl">Certificate locked</h1>
            <p className="mt-3 text-muted-foreground">
              Finish all {course.lessons.length} lessons of <span className="text-foreground">{courseTitle}</span> to unlock your certificate.
              You've completed {done}/{course.lessons.length} so far.
            </p>
            <Link to={`/courses/${instrument.id}/${course.id}`} className="mt-6 inline-block">
              <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Continue the course</Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-primary print:hidden">Congratulations</p>
            <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl print:hidden">Your certificate is ready</h1>

            <div className="mt-8 overflow-hidden rounded-2xl border border-border shadow-xl" data-testid="certificate-card">
              <CertificateArt svgRef={svgRef} name={name} courseTitle={courseTitle} tagline={course.tagline} dateStr={dateStr} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3 print:hidden">
              <Button onClick={downloadPng} data-testid="certificate-download" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Download size={16} className="mr-2" /> Download PNG
              </Button>
              <Button onClick={() => window.print()} data-testid="certificate-print" variant="outline" className="rounded-full border-border">
                <Printer size={16} className="mr-2" /> Print
              </Button>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-card p-5 print:hidden" data-testid="certificate-share">
              <p className="flex items-center gap-2 font-serif text-lg"><Share2 size={17} className="text-primary" /> Share your achievement</p>
              <p className="mt-1 text-sm text-muted-foreground">Every share opens a public, view-only copy of your certificate.</p>
              {certId && <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="certificate-public-link" className="mt-2 inline-block break-all text-xs text-primary hover:underline">{shareUrl}</a>}
              <div className="mt-4 flex flex-wrap gap-3">
                <Button onClick={nativeShare} data-testid="share-native" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  <Share2 size={15} className="mr-2" /> Share
                </Button>
                <Button onClick={copyLink} data-testid="share-copy" variant="outline" className="rounded-full border-border">
                  <Copy size={15} className="mr-2" /> Copy link
                </Button>
                <Button onClick={shareTwitter} data-testid="share-twitter" variant="outline" className="rounded-full border-border">
                  <Twitter size={15} className="mr-2" /> X / Twitter
                </Button>
                <Button onClick={shareFacebook} data-testid="share-facebook" variant="outline" className="rounded-full border-border">
                  <Facebook size={15} className="mr-2" /> Facebook
                </Button>
                <Button onClick={shareLinkedIn} data-testid="share-linkedin" variant="outline" className="rounded-full border-border">
                  <Linkedin size={15} className="mr-2" /> LinkedIn
                </Button>
                <Button onClick={shareInstagram} data-testid="share-instagram" variant="outline" className="rounded-full border-border">
                  <Instagram size={15} className="mr-2" /> Instagram
                </Button>
                <Button onClick={shareWhatsApp} data-testid="share-whatsapp" variant="outline" className="rounded-full border-border">
                  <MessageCircle size={15} className="mr-2" /> WhatsApp
                </Button>
              </div>
              {certId && <EmailShare certId={certId} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
